import { Component, Input } from '@angular/core';
import { DrawerCategoryGroup, DrawerService } from '../../../services/drawer.service';

/**
 * Summary status for the software installation row in the template info card.
 * Used to drive colour-coded icon rendering without emoji.
 */
export type InstallationSummaryStatus = 'installed' | 'partial' | 'pending' | 'failed';

/**
 * Structured installation summary passed to {@link TemplateInfoCardComponent}.
 * Replaces the old emoji-prefixed `installationSummaryBadge` string.
 */
export interface InstallationSummary {
  /** Overall installation outcome status. */
  status: InstallationSummaryStatus;
  /** Human-readable label without any emoji characters. */
  label: string;
}

/**
 * Data contract for the TemplateInfoCard component.
 * All status values must use the resource-cards vocabulary.
 * Callers that have a different status vocabulary must map before passing.
 */
export interface TemplateInfoCardData {
  /** Human-readable template name. Used as card title when cardTitle is absent. */
  name: string;
  /** Resolved cloud provider name (e.g. "Microsoft Azure"). */
  provider: string;
  /** Resolved region name (e.g. "East US"). */
  region: string;
  /** Resolved VM size / SKU name. */
  vmSize: string;
  /** Resolved storage tier name. Optional — shown when present. */
  storage?: string;
  /** Resolved OS label (e.g. "Windows Server 2019"). */
  osLabel: string;
  /** Resolved SDK names (e.g. ["Python 3.11", "Node.js 20"]). */
  sdks: string[];
  /** Resolved IDE/editor names. */
  editors: string[];
  /** Resolved miscellaneous tool names. */
  tools: string[];
  /** Resolved database names. */
  databases: string[];
  /**
   * Status value expressed in resource-cards vocabulary:
   * 'draft' | 'creating' | 'active' | 'failed' | 'snapshotted' | 'destroyed' | 'destroy-partial'.
   */
  status?: string;
  /**
   * Structured installation summary.
   * When absent, the Installation Status row is hidden.
   */
  installationSummary?: InstallationSummary;
  /** When true a loading progress bar is shown at the top of the card content. */
  isLoading?: boolean;
  /**
   * Optional override for the card title.
   * When set, data.name is shown as a labelled row inside the info grid.
   * Example: "Template Details" (used by vm-template-settings).
   */
  cardTitle?: string;
  /**
   * Optional subtitle shown below the card title.
   * Example: "Configured resource template for this class".
   */
  cardSubtitle?: string;
}

/** Internal display config for a status chip. */
interface ChipConfig {
  /** CSS class name applied to the chip element. */
  cssClass: string;
  /** Material icon ligature to render inside the chip. */
  icon: string;
  /** Text label rendered next to the icon. */
  label: string;
}

/**
 * TemplateInfoCardComponent — reusable Template Details info card.
 *
 * Renders provider, region, VM size, optional storage, OS, software stack chips,
 * optional installation status row, and a colour-coded state chip.
 *
 * @selector app-template-info-card
 * @input    data                 — {@link TemplateInfoCardData}
 * @output   installStatusClicked — emitted when the user clicks the Installation Status row
 */
@Component({
  selector: 'app-template-info-card',
  templateUrl: './template-info-card.component.html',
  styleUrls: ['./template-info-card.component.css'],
})
export class TemplateInfoCardComponent {
  /** Card data — required. */
  @Input() data!: TemplateInfoCardData;
  /**
   * Pre-built accordion groups for the software installation drawer.
   * When empty the Installation Status row is still shown (if the badge is set)
   * but the drawer body will be empty.
   */
  @Input() drawerContent: DrawerCategoryGroup[] = [];

  constructor(private readonly drawerService: DrawerService) { }

  /**
   * Opens the application-level installation status drawer via {@link DrawerService}.
   * The drawer is rendered at shell level (AppDrawerComponent inside MainLayoutComponent)
   * so it has correct z-index, native slide animation, and full-viewport backdrop.
   */
  openDrawer(): void {
    this.drawerService.open({
      title: 'Installation Status',
      titleIcon: 'verified',
      content: this.drawerContent,
    });
  }

  /**
   * Maps every resource-cards status value to its chip display configuration.
   * Lookup is O(1) — no switch/if-else ladder.
   */
  private readonly chipConfigMap: Record<string, ChipConfig> = {
    draft: { cssClass: 'chip-not-started', icon: 'cloud_queue', label: 'draft' },
    creating: { cssClass: 'chip-in-progress', icon: 'sync', label: 'creating' },
    active: { cssClass: 'chip-created', icon: 'check_circle', label: 'active' },
    failed: { cssClass: 'chip-failed', icon: 'cancel', label: 'failed' },
    snapshotted: { cssClass: 'chip-snapshotted', icon: 'verified', label: 'snapshotted' },
    destroyed: { cssClass: 'chip-pending', icon: 'delete_outline', label: 'destroyed' },
    'destroy-partial': { cssClass: 'chip-pending', icon: 'delete_outline', label: 'destroy-partial' },
  };

  private readonly fallbackChip: ChipConfig = {
    cssClass: 'chip-not-started',
    icon: 'cloud_queue',
    label: '',
  };

  /**
   * Resolves the chip display config for the current status value.
   * Falls back to the draft/not-started config for unrecognised values.
   */
  get chipConfig(): ChipConfig {
    const status = this.data?.status;
    if (!status) return this.fallbackChip;
    return this.chipConfigMap[status] ?? { ...this.fallbackChip, label: status };
  }

  /** True when at least one software category contains items. */
  get hasSoftware(): boolean {
    const d = this.data;
    return !!(d?.sdks?.length || d?.editors?.length || d?.tools?.length || d?.databases?.length);
  }

  /**
   * Resolved card title: the provided cardTitle override, or data.name as the
   * fallback when no override is set.
   */
  get resolvedTitle(): string {
    return this.data?.cardTitle ?? this.data?.name ?? '';
  }

  /**
   * When a custom cardTitle is provided the template name would not otherwise
   * appear anywhere visible, so it is shown as a dedicated info-grid row.
   */
  get showNameRow(): boolean {
    return !!this.data?.cardTitle;
  }

  /**
   * States in which installation status is meaningless because the VM does not yet
   * exist, is being created, or has already been destroyed or failed to provision.
   */
  private readonly installationHiddenStates = new Set<string>([
    'draft',
    'creating',
    'destroyed',
    'failed',
  ]);

  /**
   * Returns true when the installation summary should be visible — i.e. the template
   * is in a lifecycle state where installed software actually exists on the VM.
   */
  get showInstallationStatus(): boolean {
    return !!this.data?.installationSummary
      && !this.installationHiddenStates.has(this.data?.status ?? '');
  }

  /** Maps each {@link InstallationSummaryStatus} to its Material icon and colour class. */
  private readonly installIconMap: Record<InstallationSummaryStatus, { icon: string; cssClass: string }> = {
    installed: { icon: 'check_circle', cssClass: 'install-icon-success' },
    partial: { icon: 'warning', cssClass: 'install-icon-warn' },
    pending: { icon: 'schedule', cssClass: 'install-icon-pending' },
    failed: { icon: 'cancel', cssClass: 'install-icon-error' },
  };

  /**
   * Resolves icon name and colour CSS class for the current installation summary status.
   * Falls back to the pending config when status is absent or unrecognised.
   */
  get installIconConfig(): { icon: string; cssClass: string } {
    return this.installIconMap[this.data?.installationSummary?.status ?? 'pending'];
  }
}
